import { ApiFetchParams, ApiQueryOptions, getQueryFn, useApiQuery, useApiQueryWithPageCache } from "../helpers/useApi";
import { apiFetch } from "./fetch";

export interface DashboardsApiResponse<T> {
    dashboard: T;
}

export type DashboardingDetails = {
    name: string;
    desc: string;
    i18: {
        nameKey: string;
        descKey: string;
    }
}

export type Chart = {
    chart: {
        id: number;
        nameDetails: DashboardingDetails;
        type: string;
        style: string;
        position: number;
    },
    indicator: {
        id: number;
        name: string;
        apiPath: string;
        apiParams:string[];
        externalPath: string;
        supportedCharts: string[];
    }
}

export type Dashboard = {
    dashboard: {
        id: string;
        titleDetails: DashboardingDetails;
        categoryDetails: DashboardingDetails;
        type: string;
        accessType: string;
        favourite: boolean;
        isDefault: boolean;
        uiPageKey: string;    
    }
    charts?: Chart[];
}

export type DashboardFeedbackRequest = {
  featureKey: string;
  featureVersion: string;
  rating: number;
  comment: string;
  browser: string;
  deviceType: string;
}

export interface LandingDashboardRequest {
    period: string;
    locationIds: string[];
}

export interface LandingDashboardMetrics {
    totalSales: number;
    totalTax: number;
    overallPercentage: number;
    currency: string;
}

export function useLandingDashboard<T>(queryKey: string, period: string, locationIds: string[]) {
    const queryParams = new URLSearchParams({ period });
    locationIds.forEach((locationId) => queryParams.append('locationIds', locationId));

    return useApiQuery<T>({
        path: `/data/v1/dashboards/landing-dashboard?${queryParams.toString()}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useDashboards<T>(queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/dashboards`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useDashboard<T>(queryKey: string, dashboardId: string, options?: ApiQueryOptions<DashboardsApiResponse<T>>) {
    return useApiQuery<T>({
        path: `/data/v1/dashboards/${dashboardId}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function updateDefaultDashboard<T>(dashboardId: string | undefined, isDefault: boolean | undefined, accessToken: string | undefined): Promise<object> {
    return getQueryFn<object>({
        path: `/data/v1/dashboards/${dashboardId}/landing-dashboard`,
        body: {"isDefault": isDefault},
        method: 'PUT'
    }, (body: unknown = {}, path = '/', method = 'GET',
        endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
        authorization: string | undefined = undefined) => apiFetch<object>(path, body, method, endpoint, authorization || `Bearer ${accessToken}`), accessToken)();
}

export function updateFavoriteDashboard<T>(dashboardId: string | undefined, favourite: boolean | undefined, accessToken: string | undefined): Promise<object> {
    return getQueryFn<object>({
        path: `/data/v1/dashboards/${dashboardId}/favourite`,
        body: {isFavourite: favourite || false},
        method: 'PUT'
    }, (body: unknown = {}, path = '/', method = 'GET',
        endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
        authorization: string | undefined = undefined) => apiFetch<object>(path, body, method, endpoint, authorization || `Bearer ${accessToken}`), accessToken)();
}

export function sendDashboardsFeedback(payload: DashboardFeedbackRequest, accessToken: string | undefined): Promise<object> {
    return getQueryFn<object>({
        path: `/data/v1/dashboards/feedback`,
        body: payload || {},
        method: 'POST'
    }, (body: unknown = {}, path = '/', method = 'GET',
        endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
        authorization: string | undefined = undefined) => apiFetch<object>(path, body, method, endpoint, authorization || `Bearer ${accessToken}`), accessToken)();
}
